import {
  deltakerTypeToKvitteringField,
  getDeltakerKeys,
  isDeltakerEqual,
} from '../queries/helpers'
import { MutationKey, useMutation } from '@tanstack/react-query'
import { DeltakerTypes, IDeltaker, IDeltakerMutateVars } from '../types'
import { useCallback } from 'react'
import { DEBOUNCE_MS } from '../../../constants'
import { AxiosResponse } from 'axios'
import {
  useKvittering,
  useOptimisticDebounceKvitteringMutate,
} from '../../kvitteringer'

export {
  useVirksomhetDeltakereInfo,
  useAndreDeltakere,
  useVirksomhetDeltakere,
  useMattilsynetDeltakere,
  useAddDeltaker,
  useUpdateDeltakerNavn,
  useRemoveDeltaker,
}

const useDeltakere = (kvitteringId: string, type: DeltakerTypes) =>
  useKvittering<IDeltaker[]>(kvitteringId, {
    select: (kvittering) => {
      switch (type) {
        case DeltakerTypes.VIRKSOMHET:
          return kvittering.virksomhetsDeltagere ?? []
        case DeltakerTypes.MATTILSYNET:
          return kvittering.mattilsynetDeltagere ?? []
        case DeltakerTypes.ANDRE:
          return kvittering.andreDeltagere ?? []
        default:
          return []
      }
    },
  })

const useAndreDeltakere = (kvitteringId: string) =>
  useDeltakere(kvitteringId, DeltakerTypes.ANDRE)
const useVirksomhetDeltakere = (kvitteringId: string) =>
  useDeltakere(kvitteringId, DeltakerTypes.VIRKSOMHET)
const useMattilsynetDeltakere = (kvitteringId: string) =>
  useDeltakere(kvitteringId, DeltakerTypes.MATTILSYNET)

const useDeltakerMutation = (mutationKey: MutationKey) =>
  useMutation<
    AxiosResponse<null>,
    string | Error,
    IDeltakerMutateVars,
    { previousDeltakere: IDeltaker[] }
  >({ mutationKey })

const useUpdateDeltakerNavn = (
  deltakerType: DeltakerTypes,
  kvitteringId: string
) => {
  const updateDeltaker = useDeltakerMutation(
    getDeltakerKeys(deltakerType).update(kvitteringId)
  )

  const optimisticDebounceUpdate = useOptimisticDebounceKvitteringMutate(
    updateDeltaker.mutate,
    kvitteringId,
    (kvittering, { deltaker, type }) => {
      if (!kvittering) return kvittering

      const typeField = deltakerTypeToKvitteringField(type)

      return {
        ...kvittering,
        [typeField]: kvittering[typeField].map((d: IDeltaker): IDeltaker => {
          if (!isDeltakerEqual(d, deltaker)) return d

          return {
            ...d,
            navn: deltaker.navn,
          }
        }),
      }
    }
  )

  const updateDeltakerFn = useCallback(
    (variables: IDeltakerMutateVars) => {
      const { id } = variables.deltaker

      const updateKey = JSON.stringify(
        getDeltakerKeys(variables.type).update(variables.kvitteringId)
      )

      optimisticDebounceUpdate(variables, {
        debounceMs: DEBOUNCE_MS,
        key: `${updateKey}_${id}`,
      })
    },
    [optimisticDebounceUpdate]
  )

  return {
    ...updateDeltaker,
    mutate: updateDeltakerFn,
  }
}

const useAddDeltaker = (deltakerType: DeltakerTypes, kvitteringId: string) =>
  useDeltakerMutation(getDeltakerKeys(deltakerType).add(kvitteringId))

const useRemoveDeltaker = (deltakerType: DeltakerTypes, kvitteringId: string) =>
  useDeltakerMutation(getDeltakerKeys(deltakerType).remove(kvitteringId))

const useVirksomhetDeltakereInfo = (kvitteringId: string) => {
  const { data } = useKvittering(kvitteringId, {
    select: ({
      virksomhetsNavn,
      virksomhetsOrgNr,
      ingenTilStede,
      ingenTilStedeBegrunnelse,
    }) => ({
      virksomhetsNavn,
      virksomhetsOrgNr,
      ingenTilStede,
      ingenTilStedeBegrunnelse,
    }),
  })

  return (
    data ?? {
      virksomhetsNavn: '',
      virksomhetsOrgNr: '',
      ingenTilStede: false,
      ingenTilStedeBegrunnelse: '',
    }
  )
}
